import { useCallback, useEffect, useState } from "react";
import { BarChart3, Eye, FileText, Info, Loader2, RefreshCw, Users } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const RANGES = [
  { days: 7, label: "Last 7 days" },
  { days: 30, label: "Last 30 days" },
];

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-gold/40 bg-white/85 p-5 shadow-sm backdrop-blur" data-testid={`traffic-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-wine/55">
        <Icon className="h-4 w-4 text-gold" /> {label}
      </div>
      <p className="mt-2 font-serif text-3xl font-semibold text-burgundy">{value.toLocaleString()}</p>
    </div>
  );
}

export default function TrafficWidget() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data: res } = await api.get(`/admin/analytics/summary?days=${days}`);
      setData(res);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="max-w-4xl" data-testid="traffic-panel">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 font-serif text-xl font-semibold text-burgundy">
            <BarChart3 className="h-5 w-5 text-gold" /> Website Traffic
          </h3>
          <p className="mt-1 text-xs text-plum/55">Visitor stats for your public site, powered by Google Analytics.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-full border border-gold/40 bg-white/75 p-1" data-testid="traffic-range-toggle">
            {RANGES.map((r) => (
              <button
                key={r.days}
                onClick={() => setDays(r.days)}
                data-testid={`traffic-range-${r.days}`}
                className={`rounded-full px-4 py-1.5 text-xs font-medium transition-all ${
                  days === r.days ? "bg-burgundy text-white" : "text-wine hover:text-golddeep"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button
            onClick={load}
            disabled={loading}
            data-testid="traffic-refresh"
            className="flex items-center gap-1.5 rounded-full border border-burgundy/25 px-3 py-2 text-xs font-medium text-wine transition-all hover:border-gold hover:text-golddeep disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20" data-testid="traffic-loading">
          <Loader2 className="h-7 w-7 animate-spin text-gold" />
        </div>
      ) : error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-5 text-sm text-red-700" data-testid="traffic-error">
          {error}
        </div>
      ) : data && data.configured === false ? (
        <div className="rounded-2xl border border-gold/40 bg-white/85 p-6 text-sm text-wine/80 shadow-sm backdrop-blur" data-testid="traffic-not-configured">
          <div className="flex items-center gap-2 font-serif text-lg font-semibold text-burgundy">
            <Info className="h-5 w-5 text-gold" /> Not connected yet
          </div>
          <p className="mt-2 leading-relaxed">
            Google Analytics isn&#39;t linked to your site yet. Once your developer adds your GA4 Property ID and
            access key, your visitor numbers will appear right here — no extra steps for you.
          </p>
        </div>
      ) : data && data.error ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-800" data-testid="traffic-api-error">
          {data.error}
        </div>
      ) : data ? (
        <div className="space-y-6" data-testid="traffic-data">
          <div className="grid gap-4 sm:grid-cols-2">
            <StatCard icon={Users} label="Visitors" value={data.activeUsers} />
            <StatCard icon={Eye} label="Page Views" value={data.pageViews} />
          </div>

          <div className="rounded-2xl border border-gold/40 bg-white/85 p-5 shadow-sm backdrop-blur" data-testid="traffic-top-pages">
            <h4 className="flex items-center gap-2 font-serif text-lg font-semibold text-burgundy">
              <FileText className="h-4 w-4 text-gold" /> Top Pages
            </h4>
            {data.topPages && data.topPages.length ? (
              <ul className="mt-3 divide-y divide-gold/15">
                {data.topPages.map((p) => (
                  <li key={p.path} className="flex items-center justify-between gap-4 py-2.5" data-testid="traffic-page-row">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-wine">{p.title || p.path}</p>
                      <p className="truncate text-xs text-plum/50">{p.path}</p>
                    </div>
                    <span className="shrink-0 rounded-full bg-blush px-3 py-1 text-xs font-semibold text-burgundy">
                      {p.views.toLocaleString()} views
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-3 text-sm text-plum/55" data-testid="traffic-no-pages">
                No page views recorded in this period yet. Data can take up to 24–48 hours to appear after setup.
              </p>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
